import { useState, useEffect, useCallback, forwardRef } from 'react';
import clsx from 'clsx';
import { X } from 'lucide-react';

interface InputProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  errorMessage?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  type?: string;
  className?: string;
  allowClear?: boolean;
  onlyNumeric?: boolean;
  pattern?: RegExp;
  validate?: (value: string) => boolean | string;
  label?: string;
  onBlur?: () => void;
  comp?: 'input' | 'textarea';
  rows?: number;
  onClick?: () => void;
  autoFocus?: boolean;
  maxLength?: number;
  errorIconClearable?: boolean;
  onClear?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputClassName?: string;
  ghostText?: React.ReactNode;
  ghostTextClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      name,
      value = '',
      onChange,
      placeholder = '',
      disabled = false,
      readOnly = false,
      error: externalError = false,
      errorMessage: externalErrorMsg = '',
      prefix,
      suffix,
      icon,
      iconPosition = 'right',
      type = 'text',
      className = '',
      allowClear = false,
      onlyNumeric = false,
      pattern,
      validate,
      label,
      onBlur,
      comp = 'input',
      rows = 3,
      onClick,
      autoFocus,
      maxLength,
      errorIconClearable = true,
      onClear,
      onKeyDown,
      inputClassName = '',
      ghostText,
      ghostTextClassName = '',
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [internalError, setInternalError] = useState('');
    const [touched, setTouched] = useState(false);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value;

      if (onlyNumeric) val = val.replace(/[^\d]/g, '');

      if (validate || pattern) {
        const error = runValidation(val);
        setInternalError(error);
      }

      if (onChange) {
        onChange({ ...e, target: { ...e.target, value: val } });
      }
    };

    const handleClear = (e?: React.MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();

      if (onClear) {
        onClear();
        return;
      }

      if (onChange) {
        const event = {
          target: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
      setInternalError('');
    };

    const runValidation = useCallback(
      (val: string): string => {
        if (pattern && !pattern.test(val)) {
          return 'Invalid format';
        }
        if (validate) {
          const result = validate(val);
          if (typeof result === 'string') return result;
          if (!result) return 'Invalid input';
        }
        return '';
      },
      [validate, pattern]
    );

    const showError = externalError || internalError;
    const hasErrorIcon = showError && icon;
    const showRegularClearButton = allowClear && !!value && !disabled && !readOnly && !hasErrorIcon;

    const isErrorIconClickable =
      hasErrorIcon && errorIconClearable && !!value && !disabled && !readOnly;

    useEffect(() => {
      if (touched && (pattern || validate)) {
        setInternalError(runValidation(value));
      }
    }, [value, touched, pattern, validate, runValidation]);

    const renderIcon = () => {
      if (!icon) return null;

      const iconElement = (
        <span
          className={clsx('flex-shrink-0', iconPosition === 'left' ? 'mr-2' : 'ml-2', {
            'text-gray-500': !showError,
            'cursor-pointer hover:opacity-70 transition-opacity': isErrorIconClickable,
          })}
          onClick={isErrorIconClickable ? handleClear : undefined}
          title={isErrorIconClickable ? 'Clear input' : undefined}
        >
          {icon}
        </span>
      );

      return iconElement;
    };

    return (
      <div className="w-full">
        {label && <label className="block text-title font-medium text-gray-700">{label}</label>}
        <div
          className={clsx(
            'relative flex items-center w-full border rounded-xl transition-all px-4 h-[48px]',
            {
              'border-border-dark': !showError && !isFocused,
              'border-error': showError,
              'border-primary': isFocused && !showError,
              'bg-gray-100 cursor-not-allowed opacity-60': disabled,
            },
            className
          )}
        >
          {iconPosition === 'left' && renderIcon()}
          {prefix && <span className="mr-2 text-gray-500 flex-shrink-0">{prefix}</span>}

          <div className="relative flex-1 flex items-center min-w-0">
            {ghostText && (
              <div
                className={clsx(
                  'absolute inset-0 flex items-center pointer-events-none select-none text-body leading-normal whitespace-pre !p-0 !m-0',
                  inputClassName,
                  ghostTextClassName
                )}
                style={{ fontFamily: 'monospace' }}
              >
                {ghostText}
              </div>
            )}
            {comp === 'textarea' ? (
              <textarea
                id={id}
                name={name}
                value={value}
                rows={rows}
                onChange={handleInput as any}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                onClick={onClick}
                onFocus={() => setIsFocused(true)}
                onBlur={async () => {
                  setIsFocused(false);
                  setTouched(true);
                  await onBlur?.();
                }}
                className={clsx(
                  'flex-1 outline-none border-none bg-transparent !p-0 !m-0 text-body leading-normal text-gray-800 placeholder-gray-600 min-w-0 relative z-10 appearance-none',
                  inputClassName
                )}
                style={{ fontFamily: 'monospace' }}
                onKeyDown={onKeyDown as any}
              />
            ) : (
              <input
                ref={ref}
                id={id}
                name={name}
                type={type}
                value={value}
                onChange={handleInput}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                onClick={onClick}
                onFocus={() => setIsFocused(true)}
                onBlur={async () => {
                  setIsFocused(false);
                  setTouched(true);
                  await onBlur?.();
                }}
                className={clsx(
                  'flex-1 outline-none border-none bg-transparent !p-0 !m-0 text-body leading-normal text-gray-800 placeholder-gray-600 min-w-0 relative z-10 appearance-none',
                  inputClassName
                )}
                style={{ fontFamily: 'monospace' }}
                autoFocus={autoFocus}
                maxLength={maxLength}
                onKeyDown={onKeyDown}
              />
            )}
          </div>

          {showRegularClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="ml-2 text-gray-400 hover:text-gray-600 transition"
              title="Clear input"
              aria-label="Clear input"
            >
              <X size={16} />
            </button>
          )}

          {suffix && <span className="ml-2 text-gray-500 flex-shrink-0">{suffix}</span>}
          {iconPosition === 'right' && renderIcon()}
        </div>

        {showError && (
          <div className="text-body text-error mt-1">{externalErrorMsg || internalError}</div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
